import { useRef, useState } from "react";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { IconButton } from "@mui/material";

interface FileInputProps {
  value: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  placeholder?: string;
  dropLabel?: string;
  error?: boolean | string;
  helperText?: string;
}

const FileInput = ({
  value,
  onChange,
  accept,
  placeholder = "Haz clic para seleccionar o arrastra un archivo aquí",
  dropLabel = "Suelta el archivo aquí",
  error,
  helperText,
}: FileInputProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      onChange(droppedFile);
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onChange(e.target.files[0]);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const borderColor = error
    ? 'border-red-500 bg-red-50'
    : isDragOver
      ? 'border-[var(--primary-color)] bg-orange-50'
      : 'border-gray-300 hover:border-[var(--primary-color)] hover:bg-gray-50';

  const iconColor = error
    ? 'error'
    : isDragOver
      ? 'var(--primary-color)'
      : '#9ca3af';

  return (
    <div className="flex flex-col gap-1 w-full">
      <div
        className={`
          relative w-full h-64 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors
          ${borderColor}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleFileClick}
      >
        {value && (
          <div className="absolute top-2 right-2">
            <IconButton onClick={handleRemoveFile} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          accept={accept}
        />
        <CloudUploadIcon sx={{ fontSize: 64, color: iconColor }} />
        <p className={`mt-4 text-lg font-medium text-center px-4 ${error ? 'text-red-500' : 'text-gray-600'}`}>
          {value ? value.name : (isDragOver ? dropLabel : placeholder)}
        </p>
        {!value && !isDragOver && (
          <p className="text-sm text-gray-500 mt-2">
            o haz clic para buscar en tu equipo
          </p>
        )}
        {value && (
          <p className="text-sm text-gray-500 mt-2">
            {(value.size / (1024 * 1024)).toFixed(2)} MB
          </p>
        )}
      </div>
      {helperText && (
        <p className={`text-xs ml-3 ${error ? 'text-red-500' : 'text-gray-500'}`}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default FileInput;
